import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  PageHeader,
  Skeleton,
  Switch,
  useToast,
} from '@provenance/design';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Send, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../lib/api';

interface Settings {
  enabled: boolean;
  address: string | null;
  forwarderDomains: string[];
  chamberLive: boolean;
  serverReady: { domain: string | null; sesConfigured: boolean; outboundEmailOn: boolean };
}

interface TestStatus {
  received: boolean;
  receivedAt?: string;
  seconds?: number | null;
  diagnostics?: {
    outbound: { status: string; errorMessage: string | null } | null;
    mx: { domain: string | null; records: string[]; pointsAtSes: boolean; error?: string };
    lastSnsDeliveryAt: string | null;
    lastError: { at: string; message: string } | null;
    serverReady: Settings['serverReady'];
  };
}

const TEST_TIMEOUT_MS = 2 * 60_000;

/**
 * Mailbox settings — Chamber Admin (T-136).
 *
 * Switch the mailbox on, get the address to forward to, and prove the whole path works
 * with a real test email before telling a single exporter about it.
 */
export function MailboxSettings() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [domains, setDomains] = useState<string | null>(null);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);

  const settings = useQuery({
    queryKey: ['mailbox-settings'],
    queryFn: () => api.get<Settings>('/api/console/mailbox/settings'),
  });
  const s = settings.data;

  const save = useMutation({
    mutationFn: (input: { enabled?: boolean; forwarderDomains?: string[] }) =>
      api.post<Settings>('/api/console/mailbox/settings', input),
    onSuccess: (next, input) => {
      queryClient.setQueryData(['mailbox-settings'], next);
      if (input.enabled !== undefined) toast.success(input.enabled ? 'Mailbox switched on' : 'Mailbox switched off');
      else toast.success('Forwarder domains saved');
      setDomains(null);
    },
    onError: (error) => toast.error('Could not save', error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  const regenerate = useMutation({
    mutationFn: () => api.post<Settings>('/api/console/mailbox/address/regenerate'),
    onSuccess: (next) => {
      queryClient.setQueryData(['mailbox-settings'], next);
      setConfirmRegenerate(false);
      toast.success('New address issued', 'Update your forwarding rule — the old address no longer receives mail.');
    },
    onError: (error) => toast.error('Could not issue a new address', error instanceof ApiError ? error.message : ''),
  });

  if (settings.isLoading || !s) return <Skeleton className="h-96" />;
  const ready = s.serverReady;
  const domainsValue = domains ?? s.forwarderDomains.join(', ');

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Mailbox settings"
        lede="Forward your chamber's certificate mailbox to the address below. Every email that arrives is shown in the Mailbox; certificates from your signatories and your own staff become requests automatically."
        actions={
          <Link to="/mailbox">
            <Button variant="secondary">Open the Mailbox</Button>
          </Link>
        }
      />

      {(!ready.domain || !ready.sesConfigured) && (
        <Callout tone="warn" icon={<AlertTriangle className="size-4" />} title="Inbound email is not set up on this server yet">
          The platform team needs to finish the receiving side (Amazon SES) before mail can arrive. You can switch the mailbox on
          now; nothing will arrive until they have.
        </Callout>
      )}
      {!s.chamberLive && (
        <Callout tone="info" icon={<AlertTriangle className="size-4" />} title="Your chamber is not live yet">
          Mail will arrive and be shown in the Mailbox, but is only turned into requests once the chamber is live. The test below
          works either way.
        </Callout>
      )}

      <Card>
        <CardHeader title="Email intake" description="Off: mail to the address is still recorded in the Mailbox, but never becomes a request." />
        <div className="flex items-center justify-between gap-3">
          <span className="font-medium">{s.enabled ? 'On — certificates by email become requests' : 'Off'}</span>
          <Switch
            checked={s.enabled}
            disabled={save.isPending}
            onCheckedChange={(enabled) => save.mutate({ enabled })}
            aria-label={s.enabled ? 'Switch email intake off' : 'Switch email intake on'}
          />
        </div>

        {s.address && (
          <div className="mt-4 flex flex-col gap-2 border-t border-[var(--pv-line)] pt-4">
            <p className="pv-eyebrow text-[var(--pv-faint)]">Your chamber's address</p>
            <div className="flex flex-wrap items-center gap-2">
              <code className="pv-num break-all rounded-[var(--pv-radius-sm)] border border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] px-2.5 py-1.5 [font-size:14px]">
                {s.address}
              </code>
              <Button size="sm" variant="secondary" onClick={() => void navigator.clipboard?.writeText(s.address!).then(() => toast.success('Copied'))}>
                Copy
              </Button>
              <Button size="sm" variant="ghost" leadingIcon={<RefreshCw className="size-3.5" />} onClick={() => setConfirmRegenerate(true)}>
                Issue a new address
              </Button>
            </div>
            <p className="[font-size:12.5px] text-[var(--pv-muted)]">
              Keep it private: anyone who knows it can send mail straight into your queue. Give exporters your usual certificates
              address, and forward that mailbox here.
            </p>
          </div>
        )}
      </Card>

      {s.address && <DeliveryTest ready={ready} />}

      {s.address && (
        <Card>
          <CardHeader
            title="Forwarding from your mailbox"
            description="Set this up once on the mailbox exporters already send certificates to."
          />
          <div className="grid gap-4 [font-size:13px] md:grid-cols-3">
            <section>
              <p className="font-semibold">Microsoft 365 / Exchange</p>
              <ol className="mt-1 list-decimal pl-4 text-[var(--pv-muted)]">
                <li>Outlook on the web → Settings → Mail → Forwarding.</li>
                <li>Enable forwarding, paste the address, tick “Keep a copy”.</li>
                <li>Save. If it is refused, your IT team must allow external forwarding.</li>
              </ol>
            </section>
            <section>
              <p className="font-semibold">Outlook rule (one sender or subject)</p>
              <ol className="mt-1 list-decimal pl-4 text-[var(--pv-muted)]">
                <li>Rules → New rule → choose who or what it applies to.</li>
                <li>Action: “Forward to” (not “as attachment” — both work, plain forward is clearer).</li>
                <li>Paste the address and save.</li>
              </ol>
            </section>
            <section>
              <p className="font-semibold">Gmail / Google Workspace</p>
              <ol className="mt-1 list-decimal pl-4 text-[var(--pv-muted)]">
                <li>Settings → Forwarding and POP/IMAP → Add a forwarding address.</li>
                <li>Google emails a confirmation code to this address: it appears in your Mailbox — open it and copy the code.</li>
                <li>Choose “Forward a copy” and save.</li>
              </ol>
            </section>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Forwarder domains"
          description="Domains your mailbox forwards from, e.g. your own. Mail relayed from elsewhere is flagged for the operator — not refused."
        />
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Domains, separated by commas" className="min-w-[16rem] flex-1">
            <Input value={domainsValue} onChange={(e) => setDomains(e.target.value)} placeholder="tcca.com.au" />
          </Field>
          <Button
            variant="secondary"
            disabled={domains === null}
            loading={save.isPending}
            onClick={() =>
              save.mutate({
                forwarderDomains: domainsValue
                  .split(/[\s,]+/)
                  .map((d) => d.trim())
                  .filter(Boolean),
              })
            }
          >
            Save
          </Button>
        </div>
      </Card>

      <Dialog open={confirmRegenerate} onOpenChange={setConfirmRegenerate}>
        <DialogContent>
          <DialogHeader
            title="Issue a new address?"
            description="The current address stops receiving mail immediately. Do this if the address has leaked, then update your forwarding rule."
          />
          <DialogBody>
            <p className="[font-size:13px] text-[var(--pv-muted)]">Mail already received stays in the Mailbox.</p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmRegenerate(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={regenerate.isPending} onClick={() => regenerate.mutate()}>
              Issue a new address
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Sends a real email to the address and waits for it to come back through SES. */
function DeliveryTest({ ready }: { ready: Settings['serverReady'] }) {
  const toast = useToast();
  const [test, setTest] = useState<{ nonce: string; sentAt: string } | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  const send = useMutation({
    mutationFn: () => api.post<{ nonce: string; sentAt: string }>('/api/console/mailbox/test'),
    onSuccess: (r) => {
      setTimedOut(false);
      setTest(r);
    },
    onError: (error) => toast.error('Could not send the test', error instanceof ApiError ? error.message : ''),
  });

  const status = useQuery({
    queryKey: ['mailbox-test', test?.nonce],
    queryFn: () =>
      api.get<TestStatus>(`/api/console/mailbox/test/${test!.nonce}?sentAt=${encodeURIComponent(test!.sentAt)}`),
    enabled: Boolean(test),
    refetchInterval: (q) => (q.state.data?.received || timedOut ? false : 3_000),
  });

  useEffect(() => {
    if (!test) return;
    const timer = setTimeout(() => setTimedOut(true), TEST_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [test]);

  const result = status.data;
  const waiting = Boolean(test) && !result?.received && !timedOut;

  return (
    <Card>
      <CardHeader
        title="Test delivery"
        description="Sends a real email to your address. It travels the same way an exporter's would, appears in the Mailbox marked Test, and never becomes a request."
      />
      {!ready.outboundEmailOn && (
        <Callout tone="warn" icon={<AlertTriangle className="size-4" />} title="Outbound email is off on this server">
          The test is sent with the platform's own email, which is switched off — so it cannot be sent. Ask the platform team.
        </Callout>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          leadingIcon={<Send className="size-3.5" />}
          loading={send.isPending}
          disabled={waiting || !ready.outboundEmailOn}
          onClick={() => send.mutate()}
        >
          {test ? 'Send another test' : 'Send a test email'}
        </Button>
        <span role="status" aria-live="polite" className="[font-size:13.5px]">
          {waiting && (
            <span className="inline-flex items-center gap-1.5 text-[var(--pv-muted)]">
              <Loader2 aria-hidden className="size-4 animate-spin" /> Sent — waiting for it to arrive (usually under 30 seconds)…
            </span>
          )}
          {result?.received && (
            <span className="inline-flex items-center gap-1.5 font-medium text-[var(--pv-ok)]">
              <CheckCircle2 aria-hidden className="size-4" /> Received
              {result.seconds != null ? ` after ${result.seconds} second${result.seconds === 1 ? '' : 's'}` : ''}. Email delivery works.
            </span>
          )}
          {timedOut && !result?.received && (
            <span className="inline-flex items-center gap-1.5 font-medium text-[var(--pv-stop)]">
              <XCircle aria-hidden className="size-4" /> Not received within 2 minutes.
            </span>
          )}
        </span>
      </div>

      {timedOut && !result?.received && result?.diagnostics && <Diagnosis d={result.diagnostics} />}
    </Card>
  );
}

/** Walks the path hop by hop, and says where it stopped. */
function Diagnosis({ d }: { d: NonNullable<TestStatus['diagnostics']> }) {
  const steps: Array<{ label: string; ok: boolean | null; detail: string }> = [
    {
      label: 'Test email left Provenir',
      ok: d.outbound ? d.outbound.status === 'SENT' : null,
      detail: d.outbound
        ? d.outbound.status === 'SENT'
          ? 'Accepted by the outbound mail service.'
          : `${d.outbound.status}: ${d.outbound.errorMessage ?? ''}`
        : 'No record of it being sent.',
    },
    {
      label: `MX record for ${d.mx.domain ?? 'the inbound domain'}`,
      ok: d.mx.pointsAtSes,
      detail: d.mx.pointsAtSes
        ? `Points at ${d.mx.records[0]}.`
        : d.mx.error
          ? `Lookup failed (${d.mx.error}) — the MX record is missing or not yet visible.`
          : `Points at ${d.mx.records.join(', ') || 'nothing'}, not Amazon SES.`,
    },
    {
      label: 'Amazon SES → Provenir (SNS)',
      ok: d.lastSnsDeliveryAt ? true : false,
      detail: d.lastSnsDeliveryAt
        ? `Last notification received ${new Date(d.lastSnsDeliveryAt).toLocaleString('en-AU')}.`
        : 'Nothing has ever arrived from SNS — check the receipt rule and that the SNS subscription is confirmed.',
    },
  ];
  return (
    <div className="mt-4 flex flex-col gap-2 border-t border-[var(--pv-line)] pt-4">
      <p className="font-medium">Where it stopped</p>
      <ul className="flex list-none flex-col gap-2 p-0 [font-size:13px]">
        {steps.map((s) => (
          <li key={s.label} className="flex items-start gap-2">
            {s.ok ? (
              <CheckCircle2 aria-hidden className="mt-0.5 size-4 shrink-0 text-[var(--pv-ok)]" />
            ) : (
              <XCircle aria-hidden className="mt-0.5 size-4 shrink-0 text-[var(--pv-stop)]" />
            )}
            <span>
              <span className="font-medium">{s.label}</span>
              <span className="sr-only">{s.ok ? ' — fine' : ' — problem'}</span>
              <span className="block text-[var(--pv-muted)]">{s.detail}</span>
            </span>
          </li>
        ))}
      </ul>
      {d.lastError && (
        <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="Last error receiving mail">
          {new Date(d.lastError.at).toLocaleString('en-AU')}: <code className="break-all">{d.lastError.message}</code>
        </Callout>
      )}
      <p className="[font-size:12px] text-[var(--pv-faint)]">
        Send this to your platform team — every step above is on their side.{' '}
        <Badge tone="neutral" size="sm">
          SES {d.serverReady.sesConfigured ? 'configured' : 'not configured'}
        </Badge>
      </p>
    </div>
  );
}
